import { FC, useCallback, useEffect, useState } from 'react';
import { adminService } from '@/services/admin.service';
import type {
  CostCurrentMonth,
  CostDashboard,
  CostGranularity,
  CostPeriod,
} from '@/types/admin';
import KpiCard from '@/components/admin/KpiCard';
import BarChartCard from '@/components/admin/charts/BarChartCard';
import DonutChartCard from '@/components/admin/charts/DonutChartCard';
import ForecastChartCard from '@/components/admin/charts/ForecastChartCard';
import LineChartCard from '@/components/admin/charts/LineChartCard';
import RankTableCard from '@/components/admin/charts/RankTableCard';

const PERIODS: { value: CostPeriod; label: string }[] = [
  { value: '30d', label: 'Últimos 30 dias' },
  { value: '90d', label: 'Últimos 90 dias' },
  { value: 'month', label: 'Mês atual' },
  { value: 'year', label: 'Este ano' },
  { value: 'all', label: 'Todo o período' },
];

const GRANULARITIES: { value: CostGranularity; label: string }[] = [
  { value: 'day', label: 'Dia' },
  { value: 'week', label: 'Semana' },
];

const fmtUsd = (v: number): string => v.toLocaleString('pt-BR', { style: 'currency', currency: 'USD' });
const fmtBrl = (v: number): string => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtNumber = (v: number): string => v.toLocaleString('pt-BR');

/** "US$ 12,34" ou, com câmbio, "US$ 12,34 · R$ 61,70". */
const fmtMoney = (usd: number, rate: number | null): string =>
  rate ? `${fmtUsd(usd)} · ${fmtBrl(usd * rate)}` : fmtUsd(usd);

const pad = (n: number): string => String(n).padStart(2, '0');

/** Constrói a série do gráfico de projeção: cumulativo real do mês + linha
 *  tracejada do último ponto real até o fim do mês. */
function buildForecastData(cm: CostCurrentMonth) {
  const actual = cm.cumulative_series;
  if (actual.length === 0) return [];
  const [y, m] = actual[0].date.split('-');
  const monthEnd = `${y}-${m}-${pad(cm.days_in_month)}`;
  const points = actual.map((p) => ({
    date: p.date,
    cumulative: p.cumulative_cost_usd as number | null,
    projected: null as number | null,
  }));
  const last = points[points.length - 1];
  if (last.date === monthEnd) {
    last.projected = cm.projected_month_end_usd;
  } else {
    last.projected = last.cumulative; // conecta a tracejada ao último ponto real
    points.push({ date: monthEnd, cumulative: null, projected: cm.projected_month_end_usd });
  }
  return points;
}

const budgetAccent = (status: CostCurrentMonth['budget_status']): string | undefined => {
  if (status === 'danger') return 'var(--danger)';
  if (status === 'warning') return 'var(--warning)';
  return undefined;
};

const AdminCostReportsPage: FC = () => {
  const [period, setPeriod] = useState<CostPeriod>('30d');
  const [granularity, setGranularity] = useState<CostGranularity>('day');
  const [data, setData] = useState<CostDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (p: CostPeriod, g: CostGranularity) => {
    setIsLoading(true);
    setError(null);
    try {
      setData(await adminService.getAgentCosts(p, g));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro desconhecido');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(period, granularity);
  }, [period, granularity, fetchData]);

  const rate = data?.usd_brl_rate ?? null;
  const cm = data?.current_month;
  const h = data?.headline;

  return (
    <div className="p-6 lg:p-10">
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-content-primary">Custos de Agentes</h1>
          <p className="text-content-muted text-sm">
            Custo <strong>estimado</strong> de IA (Hub de Agentes + H.Bot), pela tabela de preços configurada.
            Reconciliar com a fatura do Azure.
          </p>
        </div>
        <div className="flex flex-wrap gap-1 bg-surface-card border border-theme-border rounded-lg p-1">
          {PERIODS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                p.value === period
                  ? 'bg-accent text-accent-contrast font-semibold'
                  : 'text-content-muted hover:text-content-primary'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg border border-theme-danger/40 bg-theme-danger/10 text-content-primary flex items-center justify-between">
          <span>Erro ao carregar custos: {error}</span>
          <button type="button" onClick={() => fetchData(period, granularity)} className="text-sm underline hover:text-accent">
            Tentar de novo
          </button>
        </div>
      )}

      {isLoading && !data && <CostSkeleton />}

      {data && cm && h && (
        <div className="flex flex-col gap-8">
          {/* Hero KPIs — sempre o mês vigente (independe do seletor) */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard
              icon="payments"
              label="Custo no mês (MTD)"
              value={fmtMoney(cm.mtd_cost_usd, rate)}
              sublabel={`${cm.month_label} · estimado`}
            />
            <KpiCard
              icon="trending_up"
              label="Projeção fim do mês"
              value={fmtMoney(cm.projected_month_end_usd, rate)}
              accentColor={budgetAccent(cm.budget_status)}
              sublabel={
                `${fmtUsd(cm.daily_rate_usd)}/dia · dia ${cm.days_elapsed}/${cm.days_in_month}` +
                (cm.budget_usd ? ` · meta ${fmtUsd(cm.budget_usd)}` : '')
              }
            />
            <KpiCard
              icon="receipt_long"
              label="Custo no período"
              value={fmtMoney(h.total_cost_usd, rate)}
              sublabel={h.period_label}
            />
            <KpiCard
              icon="token"
              label="Tokens no período"
              value={fmtNumber(h.total_tokens)}
              sublabel={`${fmtNumber(h.total_calls)} chamadas`}
            />
          </div>

          {/* Projeção do mês */}
          <ForecastChartCard
            title="Custo acumulado do mês + projeção"
            subtitle={`${cm.month_label} — projeção assume a média diária atual`}
            data={buildForecastData(cm)}
            budget={cm.budget_usd}
            budgetStatus={cm.budget_status}
            height={320}
          />

          {/* Séries com toggle dia/semana */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-content-primary">Evolução no período</h2>
              <div className="flex gap-1 bg-surface-card border border-theme-border rounded-lg p-1">
                {GRANULARITIES.map((g) => (
                  <button
                    key={g.value}
                    type="button"
                    onClick={() => setGranularity(g.value)}
                    className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                      g.value === granularity
                        ? 'bg-accent text-accent-contrast font-semibold'
                        : 'text-content-muted hover:text-content-primary'
                    }`}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <LineChartCard
                title={`Custo por ${granularity === 'week' ? 'semana' : 'dia'} (USD)`}
                data={data.series.map((p) => ({ date: p.date, cost: p.cost_usd }))}
                dataKey="cost"
                yLabel="USD"
              />
              <LineChartCard
                title={`Tokens por ${granularity === 'week' ? 'semana' : 'dia'}`}
                data={data.series.map((p) => ({ date: p.date, tokens: p.tokens_total }))}
                dataKey="tokens"
                yLabel="tokens"
              />
            </div>
          </div>

          {/* Quebras por origem e modelo */}
          <div className="grid lg:grid-cols-2 gap-4">
            <DonutChartCard
              title="Custo por origem (Hub vs H.Bot)"
              subtitle="No período selecionado"
              data={data.by_agent_type.map((t) => ({ name: t.key, value: t.cost_usd }))}
            />
            <DonutChartCard
              title="Custo por modelo"
              subtitle="No período selecionado"
              data={data.by_model.map((m) => ({ name: m.label, value: m.cost_usd }))}
            />
          </div>

          {/* Top usuários */}
          <RankTableCard
            title="Top 10 usuários por gasto"
            subtitle="No período selecionado"
            rows={data.top_users}
            columns={[
              { key: 'name', label: 'Usuário', render: (r) => r.name || r.email || '—' },
              { key: 'sector', label: 'Setor' },
              { key: 'tokens', label: 'Tokens', align: 'right', render: (r) => fmtNumber(r.tokens) },
              { key: 'cost_usd', label: 'Custo', align: 'right', render: (r) => fmtMoney(r.cost_usd, rate) },
            ]}
          />

          {/* Setores */}
          <BarChartCard
            title="Custo por setor (USD)"
            subtitle="No período selecionado"
            data={data.top_sectors.map((s) => ({ sector: s.name, cost: s.cost_usd }))}
            dataKey="cost"
            categoryKey="sector"
            layout="vertical"
            colorByIndex
          />

          <p className="text-xs text-content-muted text-right">
            Atualizado em {new Date(data.generated_at).toLocaleString('pt-BR')} · valores estimados
          </p>
        </div>
      )}
    </div>
  );
};

const CostSkeleton: FC = () => (
  <div className="flex flex-col gap-8">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((k) => (
        <div key={k} className="h-28 bg-surface-card border border-theme-border rounded-xl animate-pulse" />
      ))}
    </div>
    {[1, 2].map((s) => (
      <div key={s} className="h-72 bg-surface-card border border-theme-border rounded-xl animate-pulse" />
    ))}
  </div>
);

export default AdminCostReportsPage;
